export interface FlowNode {
  id: string;
  label: string;
  toolSlug?: string;
}

export interface FlowEdge {
  from: string;
  to: string;
}

export interface System {
  id: number;
  slug: string;
  name: string;
  oneLiner: string;
  metaDescription: string;
  description: string; // supports markdown
  article?: string;
  toolsUsed: string[];
  nodes: FlowNode[];
  edges: FlowEdge[];
  inputs: string[];
  outputs: string[];
}

export const systems: System[] = [
  {
    id: 1,
    slug: "ai-receptionist",
    name: "The AI Receptionist",
    oneLiner: "A 24/7 voice agent that qualifies leads, books meetings, and speaks 30+ languages — without a single human on the phone.",
    metaDescription: "Build a 24/7 AI receptionist using ElevenLabs and Vapi. This flow covers voice cloning, knowledge base setup, system prompting, and telephony integration for a human-grade AI phone agent.",
    article: "/articles/ai-receptionist-elevenlabs",
    toolsUsed: ["elevenlabs", "vapi"],
    nodes: [
      { id: "a", label: "Incoming Call" },
      { id: "b", label: "Voice & Persona", toolSlug: "elevenlabs" },
      { id: "c", label: "Knowledge Base", toolSlug: "elevenlabs" },
      { id: "d", label: "System Prompt & Logic", toolSlug: "vapi" },
      { id: "e", label: "Telephony Bridge", toolSlug: "vapi" },
      { id: "f", label: "Lead Qualified / Meeting Booked" },
    ],
    edges: [
      { from: "a", to: "b" },
      { from: "b", to: "c" },
      { from: "c", to: "d" },
      { from: "d", to: "e" },
      { from: "e", to: "f" },
    ],
    inputs: [
      "Brand voice or voice clone audio (30+ minutes)",
      "Company documents (pricing, FAQs, opening hours)",
      "System prompt with business rules and escalation logic",
      "Twilio account or Vapi phone number",
      "CRM integration details (e.g. HubSpot webhook)",
    ],
    outputs: [
      "24/7 conversational phone agent in your brand voice",
      "Automatic lead qualification and meeting booking",
      "Structured call data synced to your CRM",
      "Multilingual support across 30+ languages",
      "A2A compatibility for AI-to-AI handshakes",
    ],
    description: `In 2026, **the first impression of your business has moved from the lobby to the phone line**. Every missed call is a missed lead — and traditional IVR menus ("Press 1 for Sales") are actively killing conversion rates.

This flow combines ElevenLabs for human-grade voice synthesis with Vapi for ultra-low-latency orchestration to produce a voice agent that qualifies leads, books meetings, and handles objections — 24 hours a day, 7 days a week, without a single human on the phone.

## How the Flow Works

**Step 1 — Voice & Persona (ElevenLabs)**

The agent's voice is the first thing a caller hears. You have two options:

- **Pre-made voice** — choose from ElevenLabs' library of 3,000+ high-fidelity voices
- **Professional Voice Clone (PVC)** — upload 30+ minutes of clean audio to clone a specific team member's voice. The result is indistinguishable from the original.

**Step 2 — Knowledge Base (ElevenLabs)**

To prevent the agent from hallucinating, you ground it in your company's actual data. Upload your pricing documents, service catalogue, and opening hours directly into the ElevenLabs Agent dashboard. Add your FAQ URL and ElevenLabs will re-index it automatically whenever you update your site.

**Step 3 — System Prompt & Logic (Vapi)**

The system prompt is the agent's standing orders. Using **Entity-Based Prompting**, you define:

- How to handle pricing questions (refer to the pricing doc; offer a callback if not listed)
- How to handle complaints (follow a specific escalation document step-by-step)
- **A2A Logic** — if the caller is detected as another AI agent (increasingly common in 2026), the agent switches to a high-speed handshake protocol and exchanges structured JSON data rather than conversational language

**Step 4 — Telephony Bridge (Vapi)**

Vapi connects your ElevenLabs voice to a real phone number. You can buy a number directly in Vapi or import an existing Twilio number. Setting the model to **Turbo v2.5** keeps response latency under 500ms — the threshold below which conversations feel human.

**Step 5 — Lead Qualified / Meeting Booked**

Every interaction produces structured output: sentiment analysis, auto-tagged lead data, and a CRM record. If the caller was a qualified lead, a meeting is booked in your calendar automatically.

## Why This Flow Matters in 2026

> **The rise of Personal AI Agents** — customers are increasingly using their own AI (Apple Intelligence, OpenAI Operator) to make calls on their behalf. If your business relies on a human who might not pick up, or a legacy IVR that a bot can't navigate, you are being filtered out of the conversation entirely. An ElevenLabs/Vapi receptionist is *machine-friendly by design*.

| Feature | Legacy IVR | Human Receptionist | This Flow |
| --- | --- | --- | --- |
| **Availability** | 24/7 (limited) | 40 hrs/week | 24/7/365 |
| **Multilingual** | No | Limited | 30+ languages |
| **Bot-Friendly** | Poor | Medium | High (A2A ready) |
| **Data Entry** | Manual | Manual | Automatic CRM sync |
| **Cost to scale** | High | Very high | ~$0 marginal cost |

## Estimated Setup Time

A basic version of this flow — pre-made voice, simple knowledge base, no voice clone — can be live in **under 2 hours**. A full production deployment with PVC, CRM integration, and A2A logic typically takes **1–2 days**.
`,
  },
  {
    id: 2,
    slug: "faceless-youtube-channel",
    name: "Faceless YouTube Channel",
    oneLiner: "A complete AI production pipeline that takes a topic and outputs a monetisation-ready YouTube video — no camera, no studio, no editor required.",
    metaDescription: "Build a profitable faceless YouTube channel using ChatGPT, Synthesia, Runway, and Descript. This flow covers scriptwriting, AI avatar hosting, B-roll generation, and automated editing.",
    article: "/articles/automate-youtube-2026",
    toolsUsed: ["chatgpt", "synthesia", "runway", "descript"],
    nodes: [
      { id: "a", label: "Topic / Niche" },
      { id: "b", label: "Gap Analysis & Script", toolSlug: "chatgpt" },
      { id: "c", label: "AI Avatar Presenter", toolSlug: "synthesia" },
      { id: "d", label: "B-Roll Generation", toolSlug: "runway" },
      { id: "e", label: "Edit, Captions & Shorts", toolSlug: "descript" },
      { id: "f", label: "Published Video" },
    ],
    edges: [
      { from: "a", to: "b" },
      { from: "b", to: "c" },
      { from: "b", to: "d" },
      { from: "c", to: "e" },
      { from: "d", to: "e" },
      { from: "e", to: "f" },
    ],
    inputs: [
      "Niche or topic idea",
      "Target audience and channel tone",
      "Synthesia avatar and background choice",
      "Brand colour for captions",
      "Target platforms (YouTube, TikTok, Shorts)",
    ],
    outputs: [
      "Full-length YouTube video (10–20 mins)",
      "3 x vertical Shorts for TikTok / YouTube Shorts",
      "SEO-optimised title, description, and tags",
      "Thumbnail variants for A/B testing",
      "Multilingual dubbed versions (optional)",
    ],
    description: `The faceless YouTube model has matured. In 2026, **generic stock footage and robotic TTS are no longer enough to monetise** — the algorithm rewards information density, visual originality, and consistent brand identity. This flow delivers all three without a camera, a studio, or a video editor.

## How the Flow Works

**Step 1 — Gap Analysis & Script (ChatGPT)**

The biggest mistake new creators make is writing a script without first validating the topic. This flow begins with a **Gap Analysis prompt**:

\`\`\`
Act as a viral YouTube Script Architect. I am making a video about [Your Topic].
First, analyze the top 5 videos in this niche and identify Knowledge Gaps —
questions viewers are asking in the comments that remain unanswered.
Then write a 1,500-word script with a Pattern Interrupt hook in the first 5 seconds.
Structure the body with 3-5 high-density value pillars.
Include visual cue notes for B-roll every 10 seconds.
\`\`\`

The output is a script that is pre-validated against what real viewers actually want — not what you *think* they want.

**Step 2 — AI Avatar Presenter (Synthesia)**

Synthesia transforms the script into a hosted video segment. Choose an avatar, set the wardrobe and background to match your channel's brand, and paste in your script. Use **Expressive Tags** to prevent the uncanny valley effect:

| Tag | Effect |
| --- | --- |
| \`[nod]\` | Builds trust after a key fact |
| \`[eyebrow_raise]\` | Signals a surprising statistic |
| \`[smile]\` | Transitions to a positive outcome |

**Step 3 — B-Roll Generation (Runway)**

Stock footage is a red flag in 2026 — the algorithm can identify it. Runway Gen-4 creates visuals that have never existed before. Use the **Shot Grammar** formula:

> **[Camera Movement] + [Subject/Scene] + [Lighting/Style]**

Example: *"Slow drone descent: neon-lit city at midnight, rain on streets, cinematic 4K, anamorphic lens."*

**Step 4 — Edit, Captions & Shorts (Descript)**

Descript's **Underlord AI** automates the tedious editing work with single-sentence instructions:

- *"Remove all filler words and long pauses"*
- *"Add viral captions centred in frame, highlight the active word in [Brand Color]"*
- *"Identify the 3 most insightful moments and create 60-second vertical clips for TikTok"*

Studio Sound processing makes even smartphone-recorded audio feel like it was captured in a professional booth.

## Why This Flow Works

**Retention is everything.** YouTube's algorithm in 2026 prioritises *average view duration* above all other signals. This flow addresses every retention killer:

- The script eliminates information gaps (viewers stay because they're learning something new)
- Synthesia avatars with gesture tags maintain visual engagement
- Runway B-roll provides a visual cut every 10 seconds (the maximum before attention drifts)
- Descript removes every pause and filler word that causes drop-off

## Estimated Monthly Cost

| Tool | Plan | Cost |
| --- | --- | --- |
| ChatGPT | Plus | $20/mo |
| Synthesia | Starter | ~$29/mo |
| Runway | Standard | ~$12/mo |
| Descript | Hobbyist | ~$16/mo |
| **Total** | | **~$77/mo** |

At even modest monetisation (1,000 subscribers + affiliate links), this stack pays for itself within the first month.
`,
  },
  {
    id: 3,
    slug: "ai-podcasting-empire",
    name: "AI Podcasting Empire",
    oneLiner: "A full-stack podcasting workflow that takes you from a raw idea to a published episode, show notes, social clips, and a blog post — all under $70/month.",
    metaDescription: "Build a professional AI-powered podcast using Claude, Descript, ElevenLabs, and Grammarly. This flow covers scripting, editing, voice, visuals, and GEO-optimised distribution.",
    article: "/articles/podcast-on-budget",
    toolsUsed: ["claude", "descript", "elevenlabs", "grammarly", "canva", "writesonic"],
    nodes: [
      { id: "a", label: "Episode Idea" },
      { id: "b", label: "Research & Script", toolSlug: "claude" },
      { id: "c", label: "Record & Edit", toolSlug: "descript" },
      { id: "d", label: "Voice Enhancement", toolSlug: "elevenlabs" },
      { id: "e", label: "Cover Art & Branding", toolSlug: "canva" },
      { id: "f", label: "Show Notes & Blog Post", toolSlug: "writesonic" },
      { id: "g", label: "Transcript Polish", toolSlug: "grammarly" },
      { id: "h", label: "Published Episode + Content" },
    ],
    edges: [
      { from: "a", to: "b" },
      { from: "b", to: "c" },
      { from: "c", to: "d" },
      { from: "c", to: "e" },
      { from: "d", to: "f" },
      { from: "f", to: "g" },
      { from: "e", to: "h" },
      { from: "g", to: "h" },
    ],
    inputs: [
      "Episode topic or guest brief",
      "Brand guidelines and target audience",
      "Raw audio recording (or script for AI narration)",
      "Existing website or RSS feed for distribution",
    ],
    outputs: [
      "Edited, studio-quality podcast episode",
      "AI-enhanced or cloned voice for intros/outros",
      "Professional cover art and social media templates",
      "SEO and GEO-optimised show notes",
      "Full episode transcript (indexable by AI search engines)",
      "Blog post, LinkedIn newsletter, and Twitter/X threads",
      "3–5 short-form clips for TikTok and Reels",
    ],
    description: `Podcasting completion rates in 2026 are hitting **80–90%** — a figure no other content format comes close to. One well-produced episode creates more trust with a listener than a hundred social media posts. The problem has always been production cost and complexity. This flow solves both.

## Why Podcasting Is the Highest-ROI Content Format in 2026

> **B2B impact:** Strategic podcasts are reporting a 3–5x ROI within 12 months. A podcast invitation is the most powerful warm introduction to a high-value prospect in existence — they spend 45 minutes with you before ever taking a meeting.

The 2026 podcasting landscape has a second advantage: **GEO**. AI search engines (ChatGPT, Perplexity, Gemini) consume transcripts to answer user queries. A well-transcribed, well-distributed podcast episode is one of the most powerful GEO assets you can create — the AI sees it as *primary source material* from a real human expert.

## How the Flow Works

**Step 1 — Research & Script (Claude)**

Claude is the tool of choice for podcast scripting because of its **long context window** and nuanced reasoning. Where ChatGPT excels at rapid ideation, Claude excels at maintaining a consistent voice across a 3,000-word script and producing the kind of structured, citation-worthy content that GEO rewards.

Use Claude to:
- Research your topic and identify unique angles competitors haven't covered
- Generate a script with a strong opening hook and clear section structure
- Identify *"content gaps"* in existing top-ranking episodes

**Step 2 — Record & Edit (Descript)**

Descript turns a raw recording into a polished episode by letting you **edit audio the same way you edit a document**. Delete a word in the transcript and the audio is spliced automatically.

Key Descript features for podcast production:
- **Filler word removal** — eliminate every "um", "uh", and long pause with one command
- **Studio Sound** — makes any recording, including smartphone audio, sound like a professional booth
- **Eye Contact correction** — if you record video, Descript corrects your gaze to look directly at the camera

**Step 3 — Voice Enhancement (ElevenLabs)**

For intros, outros, and ad reads — or to fix a flubbed line without re-recording — ElevenLabs is unmatched. The **Overdub** feature clones your voice from your existing recordings and lets you type new audio in your own voice.

> Used by audiobook publishers and global brands to maintain consistent audio identity across thousands of hours of content.

**Step 4 — Cover Art & Branding (Canva)**

Your cover art is what makes a stranger click *Play* in a podcast directory. Canva's AI layout tools generate on-brand cover art, social media templates, and YouTube thumbnails from your existing brand assets.

**Step 5 — Show Notes & Blog Post (Writesonic)**

This is where a single recording session multiplies into a content portfolio. Writesonic takes your episode transcript and generates:
- SEO-optimised show notes with timestamps
- A full blog post (1,500–2,000 words) for your website
- A LinkedIn newsletter version
- 10 Twitter/X threads pulling out key insights

**Step 6 — Transcript Polish (Grammarly)**

Before publishing your transcript, run it through Grammarly. A clean, well-structured transcript is one of the most powerful GEO signals you can send — AI search engines treat it as *primary source documentation* from an expert author.

> **GEO Tip:** Always publish your full transcript on your website. AI engines like Gemini and Perplexity actively use podcast transcripts to answer user queries and cite the source.

## The Full Stack at a Glance

| Role | Tool | Est. Cost |
| --- | --- | --- |
| Scripting | Claude | Free–$20 |
| Editing | Descript | $16–$24 |
| Voice | ElevenLabs | Free–$5 |
| Visuals | Canva | Free–$15 |
| Distribution content | Writesonic | $39 |
| Polish | Grammarly | Free–$12 |
| **Total** | | **Under $115/mo** |

This stack replaces a **5-person production team** — writer, sound engineer, editor, social media manager, and SEO specialist.
`,
  },
  {
    id: 4,
    slug: "seo-geo-content-pipeline",
    name: "SEO & GEO Content Pipeline",
    oneLiner: "A content workflow engineered to rank on Google and be cited by AI search engines like ChatGPT, Perplexity, and Gemini — simultaneously.",
    metaDescription: "Build an SEO and GEO-optimised content pipeline using Jasper, Writesonic, Grammarly, Descript, and Browse AI. Rank on Google and get cited by AI search engines at the same time.",
    article: "/articles/seo-geo-2026",
    toolsUsed: ["jasper", "writesonic", "grammarly", "descript", "elevenlabs", "hubspot", "browse-ai"],
    nodes: [
      { id: "a", label: "Topic & Keyword Research" },
      { id: "b", label: "GEO Gap Analysis", toolSlug: "browse-ai" },
      { id: "c", label: "Fact-Dense Drafting", toolSlug: "writesonic" },
      { id: "d", label: "Brand Voice Alignment", toolSlug: "jasper" },
      { id: "e", label: "Clarity & De-Fluff", toolSlug: "grammarly" },
      { id: "f", label: "Audio/Video Layer", toolSlug: "elevenlabs" },
      { id: "g", label: "Schema Markup" },
      { id: "h", label: "Citation Monitoring", toolSlug: "hubspot" },
    ],
    edges: [
      { from: "a", to: "b" },
      { from: "b", to: "c" },
      { from: "c", to: "d" },
      { from: "d", to: "e" },
      { from: "e", to: "f" },
      { from: "e", to: "g" },
      { from: "f", to: "h" },
      { from: "g", to: "h" },
    ],
    inputs: [
      "Target topic or keyword cluster",
      "Competitor URLs to analyse",
      "Brand voice guidelines and style guide",
      "Existing content to audit",
      "Schema type requirements (Article, FAQ, HowTo, Product)",
    ],
    outputs: [
      "Long-form article optimised for both Google and AI search engines",
      "Audio narration version for voice search and podcast indexing",
      "JSON-LD Schema markup for machine readability",
      "GEO citation monitoring dashboard",
      "Content brief for future information gain opportunities",
      "Repurposed social content (LinkedIn, Twitter/X, newsletter)",
    ],
    description: `In 2026, **there are two search engines that matter**: Google, and the AI. Ranking on one no longer guarantees visibility on the other. This flow is engineered to win both simultaneously.

## The Difference Between SEO and GEO

| Feature | SEO (Traditional) | GEO (Generative) |
| --- | --- | --- |
| **User Interface** | Paginated list of links | A single synthesized answer |
| **Primary Goal** | Click-through rate | Attribution & brand authority |
| **Content Focus** | Keywords & backlinks | Factual density & semantic context |
| **Success Metric** | Organic traffic | Citation frequency in AI summaries |

> **The Zero-Click Challenge:** When ChatGPT or Perplexity answers a query, the user often never visits your website. But if your content is the *source* the AI cites, your brand authority increases regardless. GEO is brand-building at AI scale.

## How the Flow Works

**Step 1 — Topic & Keyword Research**

Start with traditional SEO keyword research (Ahrefs, Semrush, or Google Search Console). Identify a topic cluster where you can credibly provide *unique data or perspective* — not just a summary of what's already online. **Consensus content is invisible to AI.**

**Step 2 — GEO Gap Analysis (Browse AI)**

Before writing, use Browse AI to scrape the top 10 ranking results for your target query and identify what none of them answer. Set up a Browse AI robot to also monitor what ChatGPT and Perplexity currently say when asked your target question — this tells you exactly what gaps exist in the AI's current understanding.

**Step 3 — Fact-Dense Drafting (Writesonic)**

Writesonic's GEO suite is specifically built for this. Key principles:

- **Front-load every section** — place a 40–60 word summary immediately after each H2 heading. This creates *"snippet-ready"* blocks that AI models extract directly
- **Use the Q&A framework** — write subheadings as exact questions: *"How much does AI-driven SEO cost in 2026?"* rather than *"Pricing"*
- **Include original data** — a sentence like *"In our 2026 audit of 500 SaaS sites, GEO improved citation rates by 22%"* is a fact-anchor AI cannot find elsewhere

**Step 4 — Brand Voice Alignment (Jasper)**

Jasper's Brand Voice feature ensures that while you're optimising for AI chunking and snippet extraction, the article doesn't lose the human authority and unique perspective that traditional SEO still demands. *Sounding like a real expert is a trust signal for both Google and LLMs.*

**Step 5 — Clarity & De-Fluff (Grammarly)**

In a world of AI-generated noise, **linguistic clarity is a ranking signal**. Simpler, well-structured sentences are easier for LLMs to parse and cite accurately without hallucinating context. Grammarly removes the padding and ambiguity that causes AI models to skip your content.

**Step 6 — Audio/Video Layer (ElevenLabs & Descript)**

AI models are now multimodal — they index audio and video content. By creating a narrated version of your article with ElevenLabs and publishing a transcript via Descript, you create additional *surface area* for AI to find and reference your content in voice search results (Siri, Gemini Live, etc.).

**Step 7 — Schema Markup**

Move beyond basic Article schema. Use:
- \`FAQSchema\` — for Q&A sections
- \`HowToSchema\` — for step-by-step guides
- \`ProductSchema\` — for tool or service comparisons

Schema acts as a map for AI crawlers, telling them exactly what each piece of text represents.

**Step 8 — Citation Monitoring (HubSpot)**

HubSpot's AI Search Grader benchmarks how your brand is perceived by ChatGPT and Gemini. Use it to track which queries are returning your content as a citation and which are returning competitors — then update your content to close those gaps.

## The Unified Theory of Search

> The most successful content creators in 2026 are those who stop writing for *algorithms* and start writing for *answers*. SEO builds the foundation that lets search engines find you. GEO builds the reputation that forces AI models to cite you.

This flow achieves both — not as a trade-off, but as a synergy.
`,
  },
];

export const getSystemsByTool = (toolSlug: string): System[] => {
  return systems.filter(system => system.toolsUsed.includes(toolSlug));
};

export const getSystemById = (id: number): System | undefined => {
  return systems.find(system => system.id === id);
};

export const getSystemBySlug = (slug: string): System | undefined => {
  return systems.find(system => system.slug === slug);
};

export const getAllSystems = (): System[] => {
  return systems;
};
